import { CustomersController } from '@/shared/endpoints/customers';
import { ICodeDataResponse } from '@/shared/endpoints/customers/models/response';
import { createPersistSlice, getInitalStateDefField, handleApiCases, IStateDefaultField } from '../../helpers';
import { toast } from 'react-toastify';
import { ICustomerVerifyCodeResponse, ICustomersLoginResponse } from '@/shared/endpoints/customers/models/response';
import { ERROR_MESSAGES } from '@/shared/constants/error-messages';

export interface IRegisterData {
    firstName: string;
    lastName: string;
}

interface LoginStateInterface extends IStateDefaultField<ICustomersLoginResponse> {
    token: string;
    id: number | null;
    flag: boolean;
    helpersShown: string[];
    paymentInfoPopupShown: boolean;
}

export interface IAuthState {
    login: LoginStateInterface;
    intercomAuthenticatedSession: string | null;
    showSetPasswordSuccess: boolean;
    resetPassword: IStateDefaultField<ICustomerVerifyCodeResponse>;
    codeData: IStateDefaultField<ICodeDataResponse>;
}

export const initialState: IAuthState = {
    login: { ...getInitalStateDefField(), token: '', id: null, flag: false, helpersShown: [], paymentInfoPopupShown: false },
    intercomAuthenticatedSession: null,
    showSetPasswordSuccess: false,
    resetPassword: getInitalStateDefField(),
    codeData: getInitalStateDefField()
};

const authSlice = createPersistSlice(
    {
        name: 'auth',
        initialState: { ...initialState },
        reducers: {
            resetFlag: state => {
                state.login.flag = false;
            },
            setToken: (state, action: { payload: ICustomersLoginResponse }) => {
                state.login.token = action.payload.token;
                state.login.id = action.payload.id;
            },
            setIntercomAuthenticatedSession: (state, action: { payload: string | null }) => {
                state.intercomAuthenticatedSession = action.payload;
            },
            changeShowSetPasswordSuccess: (state, action: { payload: boolean }) => {
                state.showSetPasswordSuccess = action.payload;
            },
            markPaymentInfoPopupShown: state => {
                if (!state.login.paymentInfoPopupShown) {
                    state.login.paymentInfoPopupShown = true;
                }
            },
            addHelperShown: (state, action: { payload: string }) => {
                if (!state.login.helpersShown) {
                    state.login.helpersShown = [];
                }

                if (!state.login.helpersShown.includes(action.payload)) {
                    state.login.helpersShown.push(action.payload);
                }
            },
            resetAuthState: state => {
                Object.entries(initialState).forEach(([key, value]) => {
                    state[key as keyof IAuthState] = value;
                });
            }
        },
        extraReducers: builder => {
            // Register
            handleApiCases(builder, 'login', CustomersController.register, {
                onRejected(state, { payload }) {
                    let message = payload?.message || ERROR_MESSAGES.FILL_FIELDS_CORRECT;
                    if (payload && (payload.code >= '500' || payload.message.includes('PL/pgSQL'))) message = ERROR_MESSAGES.SERVER_ERROR;

                    state.login.loading = false;
                    state.login.error = true;

                    const handledBelow = message?.includes('already registered');
                    !handledBelow && toast.error(message);
                },
                config: { omitDataUpdate: true }
            });

            // Login
            handleApiCases(builder, 'login', CustomersController.login, {
                onFulfilled(state, { payload }) {
                    state.login = {
                        ...state.login,
                        token: payload.token,
                        id: payload.id
                    };
                },
                onRejected(state, { payload }) {
                    let message = ERROR_MESSAGES.LOGIN_CREDENTIALS_ERROR;
                    if (payload && payload.code >= '500') message = ERROR_MESSAGES.SERVER_ERROR;

                    state.login.loading = false;
                    state.login.error = true;
                    if (!navigator.onLine) return;
                    if (payload?.message !== ERROR_MESSAGES.NOT_VERIFIED_ACCOUNT) {
                        toast.error(message);
                    }
                },
                config: { omitDataUpdate: true }
            });

            // Logout
            handleApiCases(builder, 'login', CustomersController.logout, {
                onFulfilled(state) {
                    state.login = initialState.login;
                }
            });

            // Verify Code After Registration
            handleApiCases(builder, 'login', CustomersController.confirmCode, {
                onFulfilled(state, { payload }) {
                    // look here...
                    state.login = {
                        ...state.login,
                        token: payload.token,
                        id: payload.id
                    };
                },
                config: { omitDataUpdate: true }
            });

            // Verify code for reset password
            handleApiCases(builder, 'resetPassword', CustomersController.verifyVerificationCode, {
                config: { omitDataUpdate: true }
            });

            // Fetch code data (blacklisted – resets on every load)
            handleApiCases(builder, 'codeData', CustomersController.getCodeData, {
                onFulfilled(state, { payload }) {
                    state.codeData.data = { ...payload };
                }
            });
        }
    },
    { login: ['error', 'loading', 'flag'], resetPassword: ['error', 'loading'], codeData: ['data', 'error', 'loading'] }
);

export const {
    resetFlag,
    addHelperShown,
    markPaymentInfoPopupShown,
    resetAuthState,
    setToken,
    changeShowSetPasswordSuccess,
    setIntercomAuthenticatedSession
} = authSlice.actions;

export default authSlice;
